import { Box, Skeleton, TableBody, TableCell, TableRow, Typography } from '@mui/material';
import { externalDeliveryFileDeleteService } from '@services/private/external-delivery/file-delete.service';
import { externalDeliveryFileDetailService } from '@services/private/external-delivery/file-detail.service';
import { is200xRange } from '@utils/utilities';
import { ROUTES } from 'constants/routes';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

import Button from 'shared/components/widgets/button';
import FieldBox from 'shared/components/widgets/field-box';
import FieldBoxProperty from 'shared/components/widgets/field-box-property';
import MultipleFieldBox from 'shared/components/widgets/multiple-field-box';
import { bytesToSize } from 'shared/helpers/bytes-to-size';
import { copyTextUrl } from 'shared/helpers/copyTextUrl';
import { getClassification } from 'shared/helpers/external-delivery/get-classification';
import { getDepartment } from 'shared/helpers/external-delivery/get-department';
import { getExternalDeliveryFileList } from 'shared/helpers/external-delivery/get-external-delivery-file-list';
import { AdminConfigEnv, Classification, DepartmentCode } from 'types';

type FileData = {
  adminConfigEnv: AdminConfigEnv;
  shmDetail: {
    downloadUrl: string;
    regDt: string;
    regName: string;
    regTp: string;
    shmDeptCd: DepartmentCode;
    shmLfsKind: Classification;
    shmSeq: string;
    shmSubject: string;
    updDt: string;
    updName: string;
    updTp: string;
    uploadFileList: [
      {
        regName: string;
        regTp: string;
        shmfOriginName: string;
        shmfSeq: string;
        shmfSize: number;
      },
    ];
  } | null;
};

const ExternalDeliveryFilesDetail = ({
  fileId,
  listData,
  closeDetailAction,
}) => {
  const [fileData, setFileData] = useState<FileData>({
    adminConfigEnv: null,
    shmDetail: null,
  });
  const [isLoading, setLoading] = useState<boolean>(false)

  const getExternalDeliveryFileDetail = async (fileId: string) => {
    const { response, statusCode } =
      await externalDeliveryFileDetailService(fileId);

    if (response?.code === '1' && is200xRange(statusCode)) {
      handleFileData([
        { key: 'adminConfigEnv', value: response?.result?.adminConfigEnv },
        { key: 'shmDetail', value: response?.result?.shmDetail },
      ]);
    }
  };

  const onDeleteClick = async (encShnSeq) => {
    if(window.confirm("삭제하시겠습니까?")) {
      await deleteExternalDeliveryFile(encShnSeq);
    }
  };

  const deleteExternalDeliveryFile = async (encShmSeq) => {
    const { response, statusCode } = await externalDeliveryFileDeleteService({
      encShmSeq,
    });

    if (response?.code === '1' && is200xRange(statusCode)) {
      closeDetailAction();
      getExternalDeliveryFileList(
        listData.serviceRequiredData,
        [],
        listData.setServiceRequiredData,
        [],
      );
    }
  };

  const handleFileData = (params) => {
    setFileData((prevFileData) => {
      const newFileData = { ...prevFileData };
      params.forEach((item) => {
        newFileData[item.key] = item.value;
      });
      return newFileData;
    });
  };

  const onHandlerCopyText = async () => {
    const result = await copyTextUrl({
      type: 'external',
      url: fileData?.shmDetail?.downloadUrl as string,
      onlyUrl: false,
    })
    if (result) {
      toast.success('URL이 복사되었습니다!', {
        toastId: 'toastMsg'
      })
    }
    else {
      toast.error('URL 복사에 실패했습니다. 다시 시도해주세요.', {
        toastId: 'toastMsg'
      })
    }
  }

  const onHandlerCopyTextOnlyUrl = async () => {
    const result = await copyTextUrl({
      type: 'external',
      url: fileData?.shmDetail?.downloadUrl as string,
      onlyUrl: true,
    })
    if (result) {
      toast.success('URL이 복사되었습니다!', {
        toastId: 'toastMsg'
      })
    }
    else {
      toast.error('URL 복사에 실패했습니다. 다시 시도해주세요.', {
        toastId: 'toastMsg'
      })
    }
  }

  useEffect(() => {
    const fetchDetailData = async (id) => {
      setLoading(true)
      await getExternalDeliveryFileDetail(id);
      setLoading(false)
    }
    fetchDetailData(fileId)
  }, [fileId]);

  return (
    <Box
      padding="54px 40px 40px"
      display="flex"
      flexDirection="column"
      gap="42px"
      height="90vh"
      sx={{ overflowY: 'scroll' }}
    >
      {/* FILE DETAILS */}
      <Typography
        component={'span'} variant="h6" color="var(--base-black)" textAlign="center">
        파일 상세 정보
      </Typography>
      <Box display="flex" flexDirection="column" gap="30px" position="relative">
        <Button
          variant="outlined"
          sx={{ position: 'absolute', right: 0, top: '-24px', width: '112px' }}
          onClick={() =>
            onDeleteClick(fileData?.shmDetail?.shmSeq)
          }
        >
          삭제하기
        </Button>
        <Box display="flex" flexDirection="column">
          <Box display="flex" flexDirection="column" gap={5}>
            {/* BASIC INFORMATION */}
            <FieldBox title="기본 정보">
              {/* 1. UPLOAD TITLE */}
              <FieldBoxProperty
                isLoading={isLoading}
                name="업로드 제목"
                type="text"
                value={fileData?.shmDetail?.shmSubject ?? '--'}
              />
              {/* 2. CLASSIFICATION */}
              <FieldBoxProperty
                isLoading={isLoading}
                name="분류"
                type="text"
                value={getClassification(fileData?.shmDetail?.shmLfsKind) ?? '--'}
              />
              {/* 3. DEPARTMENT */}
              <FieldBoxProperty
                isLoading={isLoading}
                name="부서"
                type="text"
                value={getDepartment(fileData?.shmDetail?.shmDeptCd ?? '--')}
              />
              {/* 4. DOWNLOAD URL */}
              <FieldBoxProperty
                isLoading={isLoading}
                name="다운로드 URL"
                type="text"
                value={
                  /* <Box display="flex" flexDirection="column" alignItems="flex-start" gap={2}> */
                  <Box display="flex" alignItems="center" gap={2}>
                    <Typography
                      variant="body1"
                      color="var(--gray-800)"
                      /*component={'span'}
                      sx={{ wordBreak: 'break-all'}}  // URL을 길이에 맞춰 줄바꿈*/
                    >
                      {
                        fileData?.shmDetail?.downloadUrl
                        ? fileData.shmDetail.downloadUrl.length > 60
                            ? `${fileData.shmDetail.downloadUrl.slice(0, 60)}...`
                            : fileData.shmDetail.downloadUrl
                        : '---'
                      }
                    </Typography>
                    <Button
                      variant="contained"
                      color="secondary"
                      sx={{
                        padding: '14px 6px !important',
                        minHeight: '32px',
                        maxHeight: '32px',
                        fontSize: '14px',
                        lineHeight: '20px',
                        alignItems: 'center',
                      }}
                      onClick={onHandlerCopyText}
                    >
                      문구+URL 복사
                    </Button>
                    <Button
                      variant="contained"
                      color="secondary"
                      sx={{
                        padding: '14px 6px !important',
                        minHeight: '32px',
                        maxHeight: '32px',
                        fontSize: '14px',
                        lineHeight: '20px',
                        alignItems: 'center',
                      }}
                      onClick={onHandlerCopyTextOnlyUrl}
                    >
                      URL복사
                    </Button>
                  </Box>
                }
              />
            </FieldBox>

            {/* REGISTRANT INFORMATION*/}
            <FieldBox title="등록자 정보">
              {/* 1. REGISTRANT TYPE */}
              <FieldBoxProperty
                isLoading={isLoading}
                name="등록자 유형"
                type="text"
                value={
                  fileData?.shmDetail?.regTp === 'admin'
                    ? '관리자'
                    : fileData?.shmDetail?.regTp === 'staff'
                      ? '직원'
                      : '--'
                }
              />
              {/* 2. REGISTRANT NAME */}
              <FieldBoxProperty
                isLoading={isLoading}
                name="등록자"
                type="text"
                value={fileData?.shmDetail?.regName ?? '--'}
              />
              {/* 3. REGISTRATION DATE & TIME */}
              <FieldBoxProperty
                isLoading={isLoading}
                name="등록일시"
                type="text"
                value={fileData?.shmDetail?.regDt ?? '--'}
              />
              {/* 4. MODIFICATION TYPE */}
              <FieldBoxProperty
                isLoading={isLoading}
                name="수정 유형"
                type="text"
                value={
                  fileData?.shmDetail?.updTp === 'admin'
                    ? '관리자'
                    : fileData?.shmDetail?.updTp === 'staff'
                      ? '직원'
                      : ''
                }
              />
              {/* 5. MODIFIER */}
              <FieldBoxProperty
                isLoading={isLoading}
                name="수정자"
                type="text"
                value={fileData?.shmDetail?.updName ?? '--'}
              />
              {/* 6. DATE OF MODIFICATION */}
              <FieldBoxProperty
                isLoading={isLoading}
                name="수정 일시"
                type="text"
                value={fileData?.shmDetail?.updDt ?? ''}
              />
            </FieldBox>

            {/* UPLOAD FILE INFORMATION*/}

            <MultipleFieldBox title="업로드 파일 정보">
              <TableBody>
                {isLoading ?
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Skeleton height={30} />
                    </TableCell>
                  </TableRow>
                  :
                  fileData?.shmDetail?.uploadFileList?.map((uploadedFile, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        {uploadedFile?.shmfOriginName ?? '--'}
                      </TableCell>
                      <TableCell>
                        {bytesToSize(uploadedFile?.shmfSize) ?? '--'}
                      </TableCell>
                      <TableCell>
                        {uploadedFile?.regTp === 'admin'
                          ? '관리자'
                          : uploadedFile?.regTp === 'staff'
                            ? '직원'
                            : '--'}
                      </TableCell>
                      <TableCell>{uploadedFile?.regName ?? '--'}</TableCell>
                    </TableRow>
                  ))
                }
              </TableBody>
            </MultipleFieldBox>
          </Box>
        </Box>
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="center"
          gap="14px"
        >
          <Link
            state={{
              fileData,
              isUploadEdit: true,
            }}
            to={{
              pathname: `${ROUTES.EXTERNAL_DELIVERY_FILES.UPDATE_FILE.path}/${fileId}`,
            }}
          >
            <Button variant="outlined">업로드 파일 변경화면으로</Button>
          </Link>
          <Link
            state={{
              fileData,
              isUploadEdit: false,
            }}
            to={{
              pathname: `${ROUTES.EXTERNAL_DELIVERY_FILES.UPDATE_FILE.path}/${fileId}`,
            }}
          >
            <Button variant="contained" color="primary" sx={{ width: '112px' }}>
              수정하기
            </Button>
          </Link>
        </Box>
      </Box>
    </Box>
  );
};

export default ExternalDeliveryFilesDetail;
